import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import type { PlannedWorkout } from "../../api/types";
import { StrengthLog } from "../../components/StrengthLog";
import { StrengthView } from "../../components/StrengthView";
import { StructureView } from "../../components/StructureView";
import { Badge, Button, EmptyState, ErrorState, Loading, PageHead, TextArea, useToast } from "../../components/ui";
import {
  addDays,
  fmtDayHeader,
  fmtLong,
  fmtMonth,
  iso,
  parseIso,
  plannedDistance,
  plannedDuration,
  startOfMonthGrid,
  startOfWeek,
  STATUS_WORKOUT_LABEL,
  todayIso,
  weekLabel,
  WORKOUT_TONE,
  WORKOUT_TYPE_LABEL,
} from "../../lib/training";
import { formatDuration, metersToKm } from "../../lib/format";
import { useAsync } from "../../lib/useAsync";
import { Link } from "../../router/router";
import { t, tj } from "../../i18n";

function StatusBadge({ w }: { w: PlannedWorkout }) {
  if (w.status === "completed") return <Badge tone="ok">{w.rpe ? t("Concluído, esforço {rpe}", { rpe: w.rpe }) : t("Concluído")}</Badge>;
  if (w.status === "skipped") return <Badge tone="danger">{t("Não realizado")}</Badge>;
  if (w.date < todayIso()) return <Badge tone="warn">{t("Pendente")}</Badge>;
  return null;
}

function WorkoutRow({ orgId, w }: { orgId: string; w: PlannedWorkout }) {
  const dist = plannedDistance(w.planned_distance_m, w.content.distance_source);
  return (
    <li>
      <Link to={`/org/${orgId}/atleta/treinos/${w.id}`} className={`workout-row tone-${WORKOUT_TONE[w.workout_type]}${w.date === todayIso() ? " is-today" : ""}`}>
        <span className="wr-day">{fmtDayHeader(parseIso(w.date))}</span>
        <span className="wr-main">
          <strong>{w.title}</strong>
          <span className="muted">{w.content.summary_text || WORKOUT_TYPE_LABEL[w.workout_type]}</span>
        </span>
        <span className="wr-side">
          {w.activity ? <span className="num">{t("{v} feitos", { v: metersToKm(w.activity.distance_m) })}</span> : dist && <span className="num">{dist}</span>}
          <StatusBadge w={w} />
        </span>
      </Link>
    </li>
  );
}

/** Semana atual na tela inicial do atleta. */
export function WeekWorkouts({ orgId }: { orgId: string }) {
  const monday = startOfWeek(new Date());
  const list = useAsync(
    () => api.get<PlannedWorkout[]>(`/orgs/${orgId}/me/workouts`, { from: iso(monday), to: iso(addDays(monday, 6)) }),
    [orgId, iso(monday)],
  );
  const next = list.data?.find((w) => w.date >= todayIso() && w.status === "planned");
  return (
    <section className="panel" aria-labelledby="semana">
      <div className="panel-head">
        <h2 id="semana">{t("Treinos da semana")}</h2>
        <Link to={`/org/${orgId}/atleta/calendario`} className="btn btn-ghost btn-sm">
          {t("Ver calendário")}
        </Link>
      </div>
      {list.loading && <Loading />}
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}
      {list.data && list.data.length === 0 && (
        <EmptyState title={t("Nenhum treino publicado para esta semana")}>
          {t("Quando seu treinador publicar a planilha, os treinos aparecem aqui com distância, ritmo-alvo e instruções.")}
        </EmptyState>
      )}
      {list.data && list.data.length > 0 && (
        <div className="panel-body stack">
          {next && (
            <p>
              {next.start_time
                ? tj("Próximo treino: {title}, {date} às {time}.", { title: <strong>{next.title}</strong>, date: fmtLong(next.date), time: next.start_time.slice(0, 5) })
                : tj("Próximo treino: {title}, {date}.", { title: <strong>{next.title}</strong>, date: fmtLong(next.date) })}
            </p>
          )}
          <ul className="workout-list">
            {list.data.map((w) => (
              <WorkoutRow key={w.id} orgId={orgId} w={w} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ calendário */

export function AthleteCalendarPage({ orgId }: { orgId: string }) {
  const [view, setView] = useState<"week" | "month">("week");
  const [anchor, setAnchor] = useState(() => new Date());
  const start = view === "week" ? startOfWeek(anchor) : startOfMonthGrid(anchor);
  const end = addDays(start, view === "week" ? 6 : 41);
  const list = useAsync(
    () => api.get<PlannedWorkout[]>(`/orgs/${orgId}/me/workouts`, { from: iso(start), to: iso(end) }),
    [orgId, iso(start), iso(end)],
  );
  const byDay = useMemo(() => {
    const m: Record<string, PlannedWorkout[]> = {};
    for (const w of list.data ?? []) (m[w.date] ??= []).push(w);
    return m;
  }, [list.data]);
  const step = (dir: number) =>
    setAnchor(view === "week" ? addDays(anchor, 7 * dir) : new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
  const today = todayIso();

  return (
    <>
      <PageHead title={t("Calendário")} />
      <div className="cal-toolbar">
        <div className="segmented" role="group" aria-label={t("Visualização")}>
          <button type="button" aria-pressed={view === "week"} onClick={() => setView("week")}>
            {t("Semana")}
          </button>
          <button type="button" aria-pressed={view === "month"} onClick={() => setView("month")}>
            {t("Mês")}
          </button>
        </div>
        <div className="cal-nav">
          <Button size="sm" onClick={() => step(-1)} aria-label={t("Período anterior")}>
            ‹
          </Button>
          <Button size="sm" onClick={() => setAnchor(new Date())}>
            {t("Hoje")}
          </Button>
          <Button size="sm" onClick={() => step(1)} aria-label={t("Próximo período")}>
            ›
          </Button>
          <strong className="cal-range">{view === "week" ? weekLabel(start) : fmtMonth(anchor)}</strong>
        </div>
      </div>
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}
      {!list.data && list.loading && <Loading />}
      {list.data &&
        (view === "week" ? (
          list.data.length === 0 ? (
            <div className="panel">
              <EmptyState title={t("Nenhum treino nesta semana")}>{t("Os treinos publicados pelo seu treinador aparecem aqui.")}</EmptyState>
            </div>
          ) : (
            <ul className="workout-list">
              {list.data.map((w) => (
                <WorkoutRow key={w.id} orgId={orgId} w={w} />
              ))}
            </ul>
          )
        ) : (
          <div className="cal-month-wrap">
            <div className="cal-month athlete">
              {[t("Seg"), t("Ter"), t("Qua"), t("Qui"), t("Sex"), t("Sáb"), t("Dom")].map((d) => (
                <div key={d} className="cal-month-head">
                  {d}
                </div>
              ))}
              {Array.from({ length: 42 }, (_, i) => {
                const d = addDays(start, i);
                const day = iso(d);
                return (
                  <div key={day} className={`cal-day${day === today ? " is-today" : ""}${d.getMonth() !== anchor.getMonth() ? " is-outside" : ""}`}>
                    <div className="cal-day-head">
                      <span className="cal-date">{d.getDate()}</span>
                    </div>
                    <div className="cal-items">
                      {(byDay[day] ?? []).map((w) => (
                        <Link key={w.id} to={`/org/${orgId}/atleta/treinos/${w.id}`}
                          className={`session-card tone-${WORKOUT_TONE[w.workout_type]}${w.status !== "planned" ? " is-locked" : ""}`}>
                          <span className="sc-title">{w.title}</span>
                          {w.status !== "planned" && <span className="sc-type">{STATUS_WORKOUT_LABEL[w.status]}</span>}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
    </>
  );
}

/* ------------------------------------------------------------------ detalhe e registro */

export function WorkoutDetailPage({ orgId, workoutId }: { orgId: string; workoutId: string }) {
  const toast = useToast();
  const state = useAsync(() => api.get<PlannedWorkout>(`/orgs/${orgId}/me/workouts/${workoutId}`), [orgId, workoutId]);
  const [w, setW] = useState<PlannedWorkout | null>(null);
  const [rpe, setRpe] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => setW(state.data), [state.data]);

  if (state.loading && !w) return <Loading />;
  if (state.error) return <ErrorState error={state.error} />;
  if (!w) return null;

  const canRegister = w.date <= iso(addDays(new Date(), 0));
  const dist = plannedDistance(w.planned_distance_m, w.content.distance_source);
  const dur = plannedDuration(w.planned_duration_s, w.content.duration_source);

  async function act(action: "complete" | "skip" | "reopen", e?: FormEvent) {
    e?.preventDefault();
    setBusy(action);
    try {
      const body = action === "complete" ? { rpe, note: note || null } : action === "skip" ? { note: note || null } : undefined;
      const updated = await api.post<PlannedWorkout>(`/orgs/${orgId}/me/workouts/${workoutId}/${action}`, body);
      setW(updated);
      toast(action === "complete" ? t("Treino registrado como concluído.") : action === "skip" ? t("Treino registrado como não realizado.") : t("Registro desfeito."));
    } catch (err) {
      toast((err as ApiError).message, "error");
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <p style={{ marginBottom: 8 }}>
        <Link to={`/org/${orgId}/atleta/calendario`}>{t("Calendário")}</Link>
      </p>
      <PageHead
        title={w.title}
        subtitle={w.start_time
          ? t("{date} às {time}. {type}.", { date: fmtLong(w.date), time: w.start_time.slice(0, 5), type: WORKOUT_TYPE_LABEL[w.workout_type] })
          : t("{date}. {type}.", { date: fmtLong(w.date), type: WORKOUT_TYPE_LABEL[w.workout_type] })}
      />
      {(dist || dur || w.content.expected_rpe) && (
        <dl className="scoreboard">
          {dist && (
            <div className="metric">
              <dt>{t("Distância")}</dt>
              <dd>{dist}</dd>
            </div>
          )}
          {dur && (
            <div className="metric">
              <dt>{t("Duração")}</dt>
              <dd>{dur}</dd>
            </div>
          )}
          {w.content.expected_rpe && (
            <div className="metric">
              <dt>{t("Esforço esperado")}</dt>
              <dd>{w.content.expected_rpe}/10</dd>
            </div>
          )}
        </dl>
      )}
      {(dist.startsWith("≈") || dist.startsWith("≥")) && (
        <p className="muted" style={{ marginTop: 8, fontSize: "var(--step--1)" }}>
          {dist.startsWith("≈") ? t("≈ estimado pelos ritmos-alvo.") : t("≥ mínimo: parte do treino é por tempo, sem ritmo definido.")}
        </p>
      )}

      {w.content.objective && (
        <section className="section">
          <h2>{t("Objetivo")}</h2>
          <p>{w.content.objective}</p>
        </section>
      )}
      <section className="section">
        <h2>{t("Como fazer")}</h2>
        {w.content.strength ? <StrengthView value={w.content.strength} /> : <StructureView blocks={w.content.structure} />}
      </section>
      {w.content.strength && w.status !== "skipped" && (
        <section className="panel section" aria-labelledby="series">
          <div className="panel-head">
            <h2 id="series">{t("Séries realizadas")}</h2>
          </div>
          <div className="panel-body">
            {canRegister ? (
              <StrengthLog orgId={orgId} w={w} editable onChanged={setW} />
            ) : (
              <p className="muted">{t("Você poderá registrar as séries a partir do dia {date}.", { date: fmtLong(w.date) })}</p>
            )}
          </div>
        </section>
      )}
      {w.content.instructions && (
        <section className="section">
          <h2>{t("Instruções do treinador")}</h2>
          <p style={{ whiteSpace: "pre-wrap" }}>{w.content.instructions}</p>
        </section>
      )}

      <section className="panel section" aria-labelledby="registro">
        <div className="panel-head">
          <h2 id="registro">{t("Registro")}</h2>
          <StatusBadge w={w} />
        </div>
        <div className="panel-body stack">
          {w.status === "planned" ? (
            canRegister ? (
              <form className="form" onSubmit={(e) => act("complete", e)}>
                <fieldset>
                  <legend>{t("Como foi o esforço? (1 = muito leve, 10 = máximo)")}</legend>
                  <div className="rpe-picker">
                    {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                      <label key={n}>
                        <input type="radio" name="rpe" value={n} checked={rpe === n} onChange={() => setRpe(n)} />
                        <span>{n}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <TextArea label={t("Comentário para o treinador (opcional)")} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button type="submit" variant="primary" busy={busy === "complete"}>
                    {t("Marcar como concluído")}
                  </Button>
                  <Button onClick={() => act("skip")} busy={busy === "skip"}>
                    {t("Não consegui fazer")}
                  </Button>
                </div>
                <p className="muted" style={{ fontSize: "var(--step--1)" }}>
                  {tj("Prefere enviar o arquivo do relógio? {link}: ela é ligada a este treino automaticamente.", {
                    link: <Link to={`/org/${orgId}/atleta/atividades`}>{t("Importe a atividade")}</Link>,
                  })}
                </p>
              </form>
            ) : (
              <p className="muted">{t("Você poderá registrar este treino a partir do dia {date}.", { date: fmtLong(w.date) })}</p>
            )
          ) : (
            <>
              <p>
                {STATUS_WORKOUT_LABEL[w.status]}
                {w.rpe ? t(" com esforço {rpe}/10", { rpe: w.rpe }) : ""}.
              </p>
              {w.athlete_note && <p className="muted">“{w.athlete_note}”</p>}
              {w.activity && (
                <p>
                  {tj("Atividade importada: {link}, {figs}. Veja a comparação com o que foi prescrito.", {
                    link: <Link to={`/org/${orgId}/atleta/atividades/${w.activity.id}`}>{w.activity.name}</Link>,
                    figs: <span className="num">{t("{dist} em {time}", { dist: metersToKm(w.activity.distance_m), time: formatDuration(w.activity.moving_time_s) })}</span>,
                  })}
                </p>
              )}
              {w.status === "completed" && !w.rpe && w.activity && (
                <form className="form" onSubmit={(e) => act("complete", e)}>
                  <fieldset>
                    <legend>{t("Conte ao treinador como foi o esforço (1 = muito leve, 10 = máximo)")}</legend>
                    <div className="rpe-picker">
                      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                        <label key={n}>
                          <input type="radio" name="rpe2" value={n} checked={rpe === n} onChange={() => setRpe(n)} />
                          <span>{n}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <div>
                    <Button type="submit" variant="primary" busy={busy === "complete"} disabled={!rpe}>
                      {t("Registrar esforço")}
                    </Button>
                  </div>
                </form>
              )}
              <div>
                <Button onClick={() => act("reopen")} busy={busy === "reopen"}>
                  {t("Desfazer registro")}
                </Button>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
